import { useTranslation } from 'react-i18next';
import { Shimmer } from '../../../shared/Shimmer/Shimmer';
import { CardHeaderSkeleton } from '../../../shared/CardHeader/CardHeaderSkeleton';
import { usePageSize } from './PageSizeContext';
import table from './breakdownTable.module.css';
import styles from './BreakdownSkeleton.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface IBreakdownSkeletonProps {
  rows?: number;
  columns?: number;
}

const CELL_WIDTHS = ['72%', '48%', '64%', '40%', '56%', '80%', '44%', '60%'];

function widthFor(row: number, col: number): string {
  return CELL_WIDTHS[(row * 3 + col) % CELL_WIDTHS.length]!;
}

export function BreakdownSkeleton({ rows, columns = 6 }: IBreakdownSkeletonProps) {
  const { t } = useTranslation();
  const { pageSize } = usePageSize();
  const rowCount = rows ?? pageSize;

  return (
    <div aria-busy="true" aria-label={t('productivity.skeleton.loading')}>
      <CardHeaderSkeleton />

      <ScrollArea orientation="horizontal">
        <table className={table.table}>
          <thead>
            <tr>
              {Array.from({ length: columns }, (_, c) => (
                <th key={c} className={table.th}>
                  <Shimmer className={styles.headerCell} style={{ width: widthFor(1, c) }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }, (_, r) => (
              <tr key={r} className={table.tr}>
                {Array.from({ length: columns }, (_, c) => (
                  <td key={c} className={table.td}>
                    <span className={styles.cellBox}>
                      {/* Alternate rows lag slightly, so the table waves top-to-bottom. */}
                      <Shimmer
                        className={styles.cell}
                        style={{ width: widthFor(r, c) }}
                        wave={(r % 2) * 0.08}
                      />
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={columns} className={table.paginationCell}>
                <div className={table.pagination}>
                  <Shimmer className={styles.pagerControls} />
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </ScrollArea>
    </div>
  );
}
